import { z } from 'zod';

/**
 * Optional, unique "code" column shared by Department / Worksite / Shift.
 * Trims input and normalises empty/whitespace strings to `null` so the
 * nullable-unique DB index accepts many code-less rows: MySQL permits many
 * NULLs in a unique index but only one "" — so without this, every row after
 * the first that left the code blank collided on the unique key (e.g. the
 * `Shift_code_key` 500). Output is `string | null`.
 */
export const OptionalCodeSchema = z
  .string()
  .trim()
  .max(40)
  .optional()
  .nullable()
  .transform((v) => (v ? v : null));

export const PaginationSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  sort: z.string().optional(),
  order: z.enum(['asc', 'desc']).default('desc'),
});

export type Pagination = z.infer<typeof PaginationSchema>;

export const IdParamSchema = z.object({
  id: z.string().min(1),
});

export type IdParam = z.infer<typeof IdParamSchema>;

export const RoleSchema = z.enum(['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE']);
export type Role = z.infer<typeof RoleSchema>;

export const UserStatusSchema = z.enum(['ACTIVE', 'SUSPENDED', 'TERMINATED']);
export type UserStatus = z.infer<typeof UserStatusSchema>;

export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;
